import toastService from '@/utils/toastService';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

// Inicializar vfs para pdfmake v0.2.x desde distintas formas de exportación
if (pdfMake && !pdfMake.vfs) {
  const vfsFromModule =
    (pdfFonts && pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) ||
    (pdfFonts && pdfFonts.vfs) ||
    (pdfFonts && pdfFonts.default && pdfFonts.default.pdfMake && pdfFonts.default.pdfMake.vfs) ||
    (pdfFonts && pdfFonts.default && pdfFonts.default.vfs);
  if (vfsFromModule) {
    pdfMake.vfs = vfsFromModule;
  }
}

// Fallback: cargar vfs por URL (según docs antiguas) si no está disponible
const PDFMAKE_VFS_LOCAL = `${process.env.PUBLIC_URL || ''}/vendor/pdfmake/vfs_fonts.js`;
let vfsLoadingPromise = null;

function injectScript(src) {
  return new Promise((resolve, reject) => {
    try {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error(`No se pudo cargar script: ${src}`));
      document.head.appendChild(script);
    } catch (e) {
      reject(e);
    }
  });
}

async function loadVfsFromUrlOnce() {
  if (vfsLoadingPromise) return vfsLoadingPromise;
  vfsLoadingPromise = (async () => {
    if (pdfMake && pdfMake.vfs) return pdfMake.vfs;
    // Asegurar global para el script de vfs
    if (typeof window !== 'undefined') {
      window.pdfMake = pdfMake;
    }
    // Intentar ruta local en public/
    try {
      await injectScript(PDFMAKE_VFS_LOCAL);
      if (pdfMake && pdfMake.vfs) return pdfMake.vfs;
      if (typeof window !== 'undefined' && window.vfs) {
        // Algunos paquetes exponen vfs como variable global
        pdfMake.vfs = window.vfs;
        return pdfMake.vfs;
      }
      if (window && window.pdfMake && window.pdfMake.vfs) {
        pdfMake.vfs = window.pdfMake.vfs;
        return pdfMake.vfs;
      }
    } catch (_) {}
    throw new Error(`No se pudo cargar vfs_fonts desde ruta local: ${PDFMAKE_VFS_LOCAL}`);
  })();
  return vfsLoadingPromise;
}


/**
 * Generador de PDF de garantía para mantenimientos
 */
export class PDFGenerator {
  constructor() {
    this.jsPDF = null;
    this.configTaller = {};
  }

  /**
   * Genera el PDF de garantía usando pdfmake, incluyendo imágenes antes y después
   */
  async generarPDFGarantiaPdfMake(datosMantenimiento) {
    try {
      
      // Validar que vfs esté disponible con imports estáticos (v0.2.x)
      if (!pdfMake || !pdfMake.createPdf) {
        throw new Error('pdfMake no disponible');
      }
      if (!pdfMake.vfs) {
        // Diagnóstico para detectar forma de exportación de vfs_fonts con el bundler actual
        try {
          // Intentar asignación por diversas formas
          const vfsCandidate =
            (pdfFonts && pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) ||
            (pdfFonts && pdfFonts.vfs) ||
            (pdfFonts && pdfFonts.default && pdfFonts.default.pdfMake && pdfFonts.default.pdfMake.vfs) ||
            (pdfFonts && pdfFonts.default && pdfFonts.default.vfs);
          if (vfsCandidate) {
            pdfMake.vfs = vfsCandidate;
          }
        } catch {}

        // Si aún no hay vfs, cargar por URL (fallback)
        if (!pdfMake.vfs && typeof window !== 'undefined' && typeof document !== 'undefined') {
          await loadVfsFromUrlOnce();
        }

        if (!pdfMake.vfs) {
          throw new Error('No se pudo inicializar fuentes vfs de pdfmake. Copia vfs_fonts.js a public/vendor/pdfmake/vfs_fonts.js');
        }
      }

      const datos = datosMantenimiento || {};
      const cliente = datos.cliente || {};
      const vehiculo = datos.vehiculo || {};
      const servicios = datos.servicios || [];
      const repuestos = datos.repuestos || [];
      const itemsAdicionales = datos.itemsAdicionales || [];
      const ivaPorcentaje = datos.ivaPorcentaje || 19;
      const mecanicos = datos.mecanicos || [];
      const imagenesDespues = datos.imagenesDespues || [];

      // Utilidades
      const formatPrice = (n) => {
        const v = Math.round(parseFloat(n || 0));
        const neg = v < 0;
        const abs = Math.abs(v);
        const str = abs.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        return (neg ? '-$' : '$') + str;
      };

      // Normaliza texto/base64 a data URL con mime correcto
      const ensureDataUrl = (val) => {
        if (!val) return null;
        let s = String(val).trim();
        if (s.startsWith('data:')) return s;
        // Detectar SVG inline
        if (s.startsWith('<svg')) {
          return 'data:image/svg+xml;utf8,' + encodeURIComponent(s);
        }
        // Quitar posibles comillas
        s = s.replace(/^\"|\"$/g, '');
        // Heurísticas de base64 comunes
        const isLikelyBase64 = /^[A-Za-z0-9+/=\n\r]+$/.test(s) && s.length > 100;
        if (isLikelyBase64) {
          let mime = 'image/jpeg';
          if (/^iVBORw0/.test(s)) mime = 'image/png';
          else if (/^\/9j\//.test(s)) mime = 'image/jpeg';
          else if (/^R0lGOD/.test(s)) mime = 'image/gif';
          else if (/^UklGR/.test(s)) mime = 'image/webp';
          return `data:${mime};base64,${s.replace(/\s+/g, '')}`;
        }
        return null;
      };

      // Convierte dataURL WEBP/SVG a PNG (pdfmake soporta JPG/PNG)
      const rasterizeToPng = async (dataUrl) => {
        try {
          if (!dataUrl) return null;
          const isWebp = dataUrl.startsWith('data:image/webp');
          const isSvg = dataUrl.startsWith('data:image/svg');
          if (!isWebp && !isSvg) return dataUrl; // ya compatible
          await new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              try {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                const png = canvas.toDataURL('image/png');
                resolve(png);
              } catch (e) { reject(e); }
            };
            img.onerror = () => reject(new Error('No se pudo rasterizar imagen WEBP/SVG'));
            img.src = dataUrl;
          }).then((v) => (dataUrl = v));
          return dataUrl;
        } catch (e) {
          return null;
        }
      };

      const toDataUrl = async (src) => {
        try {
          if (!src) return null;
          if (typeof src === 'string') {
            const normalized = ensureDataUrl(src);
            if (normalized) {
              const maybePng = await rasterizeToPng(normalized);
              return maybePng || normalized;
            }
          }
          // Intentar fetch y convertir a dataURL
          const res = await fetch(src);
          const blob = await res.blob();
          return await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = async () => {
              const asDataUrl = reader.result;
              const maybePng = await rasterizeToPng(asDataUrl);
              resolve(maybePng || asDataUrl);
            };
            reader.readAsDataURL(blob);
          });
        } catch (e) {
          return null;
        }
      };

      const extractSrc = (img) => {
        if (!img) return null;
        if (typeof img === 'string') return img;
        // intentar propiedades comunes
        return img.base64 || img.url || img.src || img.url_storage || img.thumbnail_url || null;
      };

      // Extrae item con src y caption/descripcion
      const toImageItem = (img) => {
        if (!img) return { src: null, caption: null };
        if (typeof img === 'string') return { src: img, caption: null };
        const src = extractSrc(img);
        const caption = img.descripcion || img.caption || img.text || null;
        return { src, caption };
      };

      // Formatear fecha a dd/MM/yyyy
      const formatDate = (input) => {
        try {
          const d = input instanceof Date ? input : new Date(input);
          const dd = String(d.getDate()).padStart(2, '0');
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const yyyy = d.getFullYear();
          return `${dd}/${mm}/${yyyy}`;
        } catch {
          return '';
        }
      };

      const loadImages = async (arr) => {
        const list = Array.isArray(arr) ? arr : [];
        const items = await Promise.all(list.map(async (img) => {
          const it = toImageItem(img);
          const dataUrl = await toDataUrl(it.src);
          return dataUrl ? { src: dataUrl, caption: it.caption || null } : null;
        }));
        return items.filter(Boolean);
      };

      const imagenesAntes = datos.imagenesAntes || [];
      const imgsAntes = await loadImages(imagenesAntes);
      const imgsDespues = await loadImages(imagenesDespues);

      // Preparar items para la tabla consolidada
      const items = [];
      servicios.forEach((s) => {
        const precio = parseFloat(s.precio || 0);
        items.push({ concepto: s.nombre || 'Servicio', cantidad: 1, precio, total: precio });
      });
      repuestos.forEach((r) => {
        const cantidad = parseInt(r.cantidad || 1);
        const precio = parseFloat(r.precioUnitario || 0);
        items.push({ concepto: r.nombre || 'Repuesto', cantidad, precio, total: cantidad * precio });
      });
      itemsAdicionales.forEach((i) => {
        const cantidad = parseInt(i.cantidad || 1);
        const precio = parseFloat(i.precio_unitario || 0);
        items.push({ concepto: i.descripcion || 'Item adicional', cantidad, precio, total: cantidad * precio });
      });

      const subtotal = items.reduce((sum, it) => sum + (it.total || 0), 0);
      const iva = subtotal * (ivaPorcentaje / 100);
      const total = subtotal + iva;

      // Helper para leer claves de configuración
      const getCfg = (key, fallback = '') => {
        const val = (this.configTaller && this.configTaller[key] != null) ? String(this.configTaller[key]).trim() : '';
        return val !== '' ? val : fallback;
      };

      // Configuración del taller ya está disponible en this.configTaller

      // Construir header con logo a la izquierda y datos de sucursal a la derecha
      let logoElement = null;
      
      // Logo alineado a la izquierda - usar logo_empresa_base64 directamente
      const logoData = this.configTaller?.logo_empresa_base64;
      if (logoData && String(logoData).trim() !== '') {
        const logoSrc = await toDataUrl(logoData);
        if (logoSrc) {
          logoElement = { image: logoSrc, width: 240, alignment: 'left' };
        } else {
          logoElement = { text: '🔧', fontSize: 24, alignment: 'left' };
        }
      } else {
        // Icono de herramienta por defecto alineado a la izquierda
        logoElement = {
          text: '🔧',
          fontSize: 24,
          alignment: 'left'
        };
      }
      
      // Información del taller alineada a la derecha usando claves
      const companyInfo = [];

      const nombreTaller = getCfg('nombre_empresa', '');
      if (nombreTaller) {
        companyInfo.push({ text: nombreTaller, style: 'companyName', alignment: 'right' });
      }
      const direccionTaller = getCfg('direccion_empresa', '');
      if (direccionTaller) {
        companyInfo.push({ text: direccionTaller, style: 'companyInfo', alignment: 'right' });
      }
      const telefonoTaller = getCfg('telefono_empresa', '');
      if (telefonoTaller) {
        companyInfo.push({ text: telefonoTaller, style: 'companyInfo', alignment: 'right' });
      }
      const emailTaller = getCfg('email_empresa', '');
      if (emailTaller) {
        companyInfo.push({ text: emailTaller, style: 'companyInfo', alignment: 'right' });
      }

      // Header en dos columnas
      const headerContent = {
        columns: [
          {
            width: 260,
            stack: [logoElement]
          },
          {
            width: '*',
            stack: companyInfo
          }
        ],
        margin: [0, 0, 0, 10]
      };

      const docDefinition = {
        info: { title: 'Certificado de Garantía' },
        pageSize: 'LETTER',
        pageMargins: [30, 20, 30, 20],
        content: [
          // Header con logo izquierda y datos sucursal derecha
          headerContent,

          // Título principal
          { text: 'CERTIFICADO DE GARANTÍA', style: 'mainTitle', alignment: 'center', margin: [0, 0, 0, 10] },

          // Información en dos columnas como el template
          {
            columns: [
              [
                { text: `Fecha: ${formatDate(new Date())}` , margin: [0, 0, 0, 2] },
                { text: `Vehículo: ${vehiculo.marca_local || vehiculo.marca || 'N/A'} ${vehiculo.modelo_local || vehiculo.modelo || 'N/A'} - ${vehiculo.patente || 'N/A'}`, margin: [0, 0, 0, 2] },
                { text: `Año: ${vehiculo.año || 'N/A'}`, margin: [0, 0, 0, 2] },
                { text: `Kilometraje: ${datos.kilometrajeActual || 'N/A'} KM`, margin: [0, 0, 0, 2] },
                ...(mecanicos.length ? [{ text: `Mecánico(s): ${mecanicos.map(m => m.nombre || m).join(', ')}`, margin: [0, 0, 0, 2] }] : []),
              ],
              [
                { text: 'Período de Garantía: 6 meses', style: 'bold', margin: [0, 0, 0, 2] },
                { 
                  text: `Vencimiento de Garantía: ${formatDate(new Date(Date.now() + 6 * 30 * 24 * 60 * 60 * 1000))}`, 
                  style: 'bold',
                  margin: [0, 0, 0, 2]
                },
                { text: `Cliente: ${(cliente.nombre || '') + ' ' + (cliente.apellido || '')}`.trim() || 'N/A', margin: [0, 0, 0, 2] },
                ...(cliente.telefono ? [{ text: `Teléfono: ${cliente.telefono}`, margin: [0, 0, 0, 2] }] : []),
                ...(cliente.email ? [{ text: `Email: ${cliente.email}`, margin: [0, 0, 0, 2] }] : []),
              ],
            ],
            columnGap: 30,
            margin: [0, 0, 0, 10],
          },

          // Tabla de detalles exactamente como el template
          ...(items.length ? [
            {
              table: {
                widths: ['*', 80, 80, 80],
                headerRows: 1,
                body: [
                  // Header con fondo azul exacto del template
                  [
                    { text: 'Concepto', style: 'tableHeader' }, 
                    { text: 'Cantidad', style: 'tableHeader', alignment: 'center' }, 
                    { text: 'Precio Unitario', style: 'tableHeader', alignment: 'right' }, 
                    { text: 'Total', style: 'tableHeader', alignment: 'right' }
                  ],
                  ...items.map((it, index) => [
                    { text: it.concepto, fillColor: index % 2 === 0 ? '#f9fafb' : null },
                    { text: it.cantidad.toString(), alignment: 'center', fillColor: index % 2 === 0 ? '#f9fafb' : null },
                    { text: formatPrice(it.precio), alignment: 'right', fillColor: index % 2 === 0 ? '#f9fafb' : null },
                    { text: formatPrice(it.total), alignment: 'right', fillColor: index % 2 === 0 ? '#f9fafb' : null },
                  ]),
                ],
              },
              layout: {
                hLineWidth: function(i) { return i === 1 ? 1 : 0.5; },
                vLineWidth: function() { return 0.5; },
                hLineColor: function() { return '#e5e7eb'; },
                vLineColor: function() { return '#e5e7eb'; },
                fillColor: function(rowIndex) { 
                  return rowIndex === 0 ? '#1d4ed8' : null;
                }
              },
              margin: [0, 0, 0, 8]
            },
            // Totales como en el template (alineados a la derecha)
            {
              columns: [
                { text: '', width: '*' },
                {
                  table: {
                    widths: [100, 92],
                    body: [
                      [{ text: 'Subtotal:', alignment: 'right' }, { text: formatPrice(subtotal), alignment: 'right' }],
                      [{ text: `IVA (${ivaPorcentaje}%):`, alignment: 'right' }, { text: formatPrice(iva), alignment: 'right' }],
                      [{ text: 'Total:', bold: true, alignment: 'right' }, { text: formatPrice(total), bold: true, alignment: 'right' }],
                    ],
                  },
                  layout: 'noBorders',
                  width: 200,
                },
              ],
              margin: [0, 0, 0, 5]
            },
          ] : []),

          // Observaciones (mover antes de imágenes)
          ...(datos.observaciones ? [
            { text: 'Observaciones', style: 'sectionHeader', margin: [0, 0, 0, 3] },
            {
              table: {
                widths: ['*'],
                body: [
                  [{ text: datos.observaciones, fillColor: '#f3f4f6', margin: [6, 0, 6, 6], fontSize: 9 }]
                ]
              },
              layout: 'noBorders',
              margin: [0, 0, 0, 6]
            },
          ] : []),

          // Imágenes ANTES (hasta 4, 2 por fila)
          ...(imgsAntes.length ? [
            { text: 'Imágenes antes del mantenimiento', style: 'sectionHeader', margin: [0, 5, 0, 4] },
            ...Array.from({ length: Math.ceil(Math.min(imgsAntes.length, 4) / 2) }).map((_, rowIdx) => ({
              columns: imgsAntes.slice(rowIdx * 2, rowIdx * 2 + 2).map((item) => ({
                width: 260,
                stack: [
                  { image: item.src, width: 260, margin: [0, 0, 2, 2] },
                  ...(item.caption ? [{ text: item.caption, fontSize: 8, alignment: 'center', color: '#6b7280', margin: [0, 0, 0, 2] }] : [])
                ]
              })),
              columnGap: 10,
              margin: [0, 0, 0, 8]
            })),
          ] : []),

          // Imágenes Después (solo si hay, hasta 4, 2 por fila)
          ...(imgsDespues.length ? [
            { text: 'Imágenes después del mantenimiento', style: 'sectionHeader', margin: [0, 5, 0, 4] },
            ...Array.from({ length: Math.ceil(Math.min(imgsDespues.length, 4) / 2) }).map((_, rowIdx) => ({
              columns: imgsDespues.slice(rowIdx * 2, rowIdx * 2 + 2).map((item) => ({
                width: 260,
                stack: [
                  { image: item.src, width: 260, margin: [0, 0, 2, 2] },
                  ...(item.caption ? [{ text: item.caption, fontSize: 8, alignment: 'center', color: '#6b7280', margin: [0, 0, 0, 2] }] : [])
                ]
              })),
              columnGap: 10,
              margin: [0, 0, 0, 8]
            })),
          ] : []),

          // Términos y Condiciones de Garantía (compactos)
          {
            text: 'Términos y Condiciones de Garantía',
            style: 'sectionHeader',
            margin: [0, 8, 0, 4]
          },
          {
            table: {
              widths: ['*'],
              body: [
                [{
                  stack: [
                    { text: 'Esta garantía cubre defectos de fabricación y mano de obra por el período especificado.', fontSize: 8, margin: [0, 0, 0, 2] },
                    { text: 'La garantía no cubre daños por mal uso, accidentes o desgaste normal.', fontSize: 8, margin: [0, 0, 0, 2] },
                    { text: 'Para hacer válida la garantía, presente este certificado junto con la factura original.', fontSize: 8, margin: [0, 0, 0, 2] },
                    { text: 'La garantía es válida únicamente en nuestro taller autorizado.', fontSize: 8, margin: [0, 0, 0, 0] }
                  ],
                  fillColor: '#f3f4f6',
                  margin: [6, 6, 6, 6]
                }]
              ]
            },
            layout: 'noBorders',
            margin: [0, 0, 0, 12]
          },

          // Firma y fecha en la misma línea
          {
            columns: [
              {
                stack: [
                  { text: '_________________________', alignment: 'center', margin: [0, 0, 0, 2] },
                  { text: 'Firma Autorizada', alignment: 'center', fontSize: 8, color: '#6b7280' }
                ],
                width: 150
              },
              { text: '', width: '*' },
              {
                text: `${this.configTaller.direccion_empresa?.split(',')[0] || 'Santiago'}, ${formatDate(new Date())}`,
                alignment: 'right',
                fontSize: 8,
                color: '#6b7280',
                margin: [0, 10, 0, 0]
              }
            ]
          },
        ],
        styles: {
          companyName: { fontSize: 14, bold: true, color: '#111827' },
          companyInfo: { fontSize: 10, color: '#6b7280' },
          mainTitle: { fontSize: 14, bold: true, color: '#111827' },
          sectionHeader: { fontSize: 11, bold: true, color: '#111827' },
          tableHeader: { fontSize: 9, bold: true, color: '#ffffff', fillColor: '#1d4ed8' },
          bold: { bold: true },
        },
        defaultStyle: { fontSize: 10, lineHeight: 1.2, color: '#374151' },
      };

      // Crear documento PDF
      const pdfDoc = pdfMake.createPdf(docDefinition);

      // Descargar archivo con timestamp para evitar cache
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const blob = await new Promise((resolve) => pdfDoc.getBlob(resolve));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Certificado_Garantia_${timestamp}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      

      // Obtener base64 para almacenar
      const base64 = await new Promise((resolve) => pdfDoc.getBase64((b64) => resolve(`data:application/pdf;base64,${b64}`)));

      toastService.success('Certificado de Garantía generado correctamente');
      return { base64 };
    } catch (error) {
      toastService.error('Error al generar el PDF de garantía (pdfmake)');
      throw error;
    }
  }

  /**
   * Inicializa el generador (mantiene compatibilidad)
   */
  async init() {
    // Solo mantenemos pdfMake, jsPDF removido
  }

  /**
   * Establece la configuración del taller
   */
  setConfigTaller(config) {
    // Aceptar: Array directo o { data: Array }
    const rows = Array.isArray(config) ? config : (config && Array.isArray(config.data) ? config.data : []);
    let configObj = {};
    rows.forEach((item, index) => {
      if (item && item.clave != null) {
        configObj[item.clave] = item.valor;
      }
    });

    this.configTaller = configObj;
  }

  /**
   * Formatea un número como precio con separadores de miles
   */
  formatPrice(number) {
    const rounded = Math.round(parseFloat(number) || 0);
    
    // Manejar números negativos
    const isNegative = rounded < 0;
    const absoluteValue = Math.abs(rounded);
    
    // Convertir a string y agregar separadores de miles
    const formatted = absoluteValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
    // Agregar signo negativo si es necesario
    return isNegative ? `-$${formatted}` : `$${formatted}`;
  }

  /**
   * Genera el PDF de garantía usando pdfMake
   */
  async generarPDFGarantia(datosMantenimiento) {
    // Delegamos al método pdfMake que es el único que usaremos
    return await this.generarPDFGarantiaPdfMake(datosMantenimiento);
  }

}

export default PDFGenerator;